import { Injectable, inject } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { DetailedFixtureFactory, FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { GridFavouriteService } from '@frontend/sports/grid/core/feature/favourites';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { Observable, of, tap } from 'rxjs';

import { GridBaseFactory } from './grid-base.factory';
import { GridFixtureFactory } from './grid-fixture.factory';

export type GridSourceEvent = EventModel | Fixture;

export interface EventOption {
    marketGrouping?: boolean;
    isPriceBoosted?: boolean;
    excludePriceboostedMarketGrouping?: boolean;
}

export interface BaseFactory {
    canCreate(events: GridSourceEvent[]): boolean;

    create(events: GridSourceEvent[], options?: EventOption): Observable<GridEvent[]>;
}

@Injectable({ providedIn: 'root' })
export class EventFactory {
    private favouriteService = inject(GridFavouriteService);
    private fixtureFactory = inject(FixtureFactory);
    private detailedFactory = inject(DetailedFixtureFactory);
    private grouping = inject(OfferGroupingService);

    private factories: BaseFactory[];

    constructor() {
        this.factories = [new GridFixtureFactory(this.fixtureFactory, this.detailedFactory, this.grouping), new GridBaseFactory()];
    }

    create(events: GridSourceEvent[] | undefined, options?: EventOption): Observable<GridEvent[]> {
        if (!events?.length) {
            return of([]);
        }

        for (const factory of this.factories) {
            if (factory.canCreate(events)) {
                return factory.create(events, options).pipe(
                    tap((mapped) => {
                        this.setFavourited(mapped);
                    }),
                );
            }
        }

        throw new Error('No market factory found for market');
    }

    private setFavourited(events: GridEvent[]): void {
        // eslint-disable-next-line no-param-reassign
        events.forEach((current) => (current.favourited = this.favouriteService.isFavourited(current)));
    }
}
